import { useEffect, useSyncExternalStore } from "react";
import { recordWarRoomEvent } from "@/lib/warRoom/warRoomEngine";

/**
 * WorldCupShowcaseMode — lightweight global store.
 * Controls the curated demo sequence + creator/streamer visual flags.
 */
export type ShowcaseState = {
  active: boolean;
  playing: boolean;
  beatIndex: number;
  creatorMode: boolean;
};

const STORAGE = "vs_showcase_mode";
let state: ShowcaseState = {
  active: false,
  playing: false,
  beatIndex: 0,
  creatorMode: false,
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function load() {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(STORAGE);
    if (raw) state = { ...state, ...JSON.parse(raw), playing: false };
  } catch { /* noop */ }
}
function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE,
      JSON.stringify({ active: state.active, creatorMode: state.creatorMode }),
    );
  } catch { /* noop */ }
}
load();

function set(patch: Partial<ShowcaseState>) {
  state = { ...state, ...patch };
  persist();
  emit();
}

export const showcase = {
  start() { set({ active: true, playing: true, beatIndex: 0 }); recordWarRoomEvent("showcaseStarts"); },
  pause() { set({ playing: false }); },
  resume() { set({ playing: true }); },
  next(total: number) {
    const idx = (state.beatIndex + 1) % Math.max(1, total);
    set({ beatIndex: idx });
  },
  prev(total: number) {
    const idx = (state.beatIndex - 1 + total) % Math.max(1, total);
    set({ beatIndex: idx });
  },
  stop() { set({ active: false, playing: false, beatIndex: 0 }); },
  toggleCreator() { set({ creatorMode: !state.creatorMode }); },
  get() { return state; },
};

const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => state;
const ssrSnap = () => state;

export function useShowcaseMode() {
  return useSyncExternalStore(subscribe, snap, ssrSnap);
}

/** Auto-enable creator mode via ?creator=1 */
export function useShowcaseUrlBootstrap() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("creator") === "1" && !state.creatorMode) set({ creatorMode: true });
    if (sp.get("autoplay") === "1" && !state.active) showcase.start();
  }, []);
}
